import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, X } from 'lucide-react';

/**
 * UpdatePrompt — shows a floating banner when a new Service Worker is waiting.
 * Clicking "Update Now" posts SKIP_WAITING; the new SW takes control and
 * index.js reloads the page so the user sees the freshly-shipped features.
 */
const UpdatePrompt = () => {
  const [registration, setRegistration] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setRegistration(e.detail);
      setDismissed(false); // Re-show if a later update arrives in the same session
    };
    window.addEventListener('pwaUpdateAvailable', handler);
    return () => window.removeEventListener('pwaUpdateAvailable', handler);
  }, []);

  if (!registration || dismissed) return null;

  const handleUpdate = () => {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Fallback: just reload
      window.location.reload();
    }
  };

  return (
    <div
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[200] w-[92%] max-w-md"
      data-testid="pwa-update-prompt"
    >
      <div className="rounded-2xl border-2 border-[#1E5EFF] bg-white shadow-2xl p-4 flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-[#1E5EFF] flex-shrink-0">
          <Sparkles size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-[#0B1F3A] text-sm">New version available</p>
          <p className="text-xs text-slate-600 font-medium mt-0.5">
            Fresh features just shipped. Update to enjoy the latest AegisPay.
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleUpdate}
              className="h-9 rounded-lg bg-[#1E5EFF] hover:bg-[#1a4fd8] text-white font-bold text-xs px-3"
              data-testid="pwa-update-now-btn"
            >
              Update Now
            </Button>
            <Button
              onClick={() => setDismissed(true)}
              variant="outline"
              className="h-9 rounded-lg text-xs px-3 font-semibold"
              data-testid="pwa-update-later-btn"
            >
              Later
            </Button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 -mt-1 -mr-1 text-slate-400 hover:text-slate-600"
          aria-label="Dismiss"
          data-testid="pwa-update-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default UpdatePrompt;
